import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { concat, find, first, get, indexOf, join, map, size, uniqBy } from 'es-toolkit/compat';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { getModel } from 'api/platform';
import { AnnotationAssignment } from 'interfaces/annotation';
import { modelIdFromModelUrl } from 'utils/queryHooks/useModelTypeOptions';
import { useAnnotationAssignmentForExecution } from 'utils/useAnnotationAssignments';
import { ActionStepper } from '../../ProceduresPage/Actions/ActionStepper';
import { SelectModelStep, StainingMethodSelectionMode } from '../../ProceduresPage/Actions/SelectModelStep';
import { PreviewTrainingPlanStep } from './PreviewTrainingPlanStep';
import { SelectAnnotationAssignmentsStep } from './SelectAnnotationAssignmentsStep';
import { SelectSlidesStep } from './SelectSlidesStep';
import { TargetClassesForTrainingStep } from './TargetClassesForTrainingStep';
import { defaultValues, IFormValues, stepsOrder, TrainingStep, validationSchema } from './types';

interface Props {
  studyId: string;
  debug?: boolean;
  studyAnnotationAssignments?: AnnotationAssignment[];
}

const TrainingDialog: React.FC<Props> = ({ studyId, debug, studyAnnotationAssignments }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const currentValidationSchema = validationSchema[stepsOrder[activeStep]];

  const formMethods = useForm<Partial<IFormValues>>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues,
  });

  const {
    watch,
    formState: { errors },
  } = formMethods;

  const checkValidationAndSetIsStepFailed = async (step: TrainingStep, objectToValidate: Record<string, any>) => {
    const stepIndex = indexOf(stepsOrder, step);
    try {
      validationSchema[step].validateSync(objectToValidate);
      setIsStepFailed((prev) => ({ ...prev, [stepIndex]: false }));
      return false;
    } catch (error) {
      console.error(`Step ${stepIndex + 1} (${step}) failed: ${error}`);
      if (debug) {
        console.error(error);
        console.debug({ objectToValidate, schema: validationSchema[step] });
      }
      setIsStepFailed((prev) => ({ ...prev, [stepIndex]: true }));
      return true;
    }
  };

  const baseModelUrl = watch('baseModel');

  const setIsModelSelectionStepFailed = () => {
    checkValidationAndSetIsStepFailed(TrainingStep.ModelSelection, {
      stainId: watch('stainId'),
      model: baseModelUrl,
      modelType: watch('modelType'),
    });
  };

  const {
    annotationAssignments: annotationAssignmentsFromQuery,
    isAssignmentIdsLoading: isLoadingAnnotationAssignments,
  } = useAnnotationAssignmentForExecution({
    stainingMethod: watch('stainingMethod'),
    skipProcedureParams: true,
    disabled: !watch('stainingMethod'),
  });

  const annotationAssignments = useMemo(
    () =>
      uniqBy(concat(annotationAssignmentsFromQuery || [], studyAnnotationAssignments || []), 'annotationAssignmentId'),
    [annotationAssignmentsFromQuery, studyAnnotationAssignments]
  );

  const modelId = modelIdFromModelUrl(baseModelUrl);
  const {
    data: modelDetailsResponse,
    isLoading: isLoadingModelDetailsResponse,
    error: modelDetailsError,
  } = useQuery({
    queryKey: ['model', modelId],
    queryFn: ({ signal }) => getModel(modelId, signal),
    retry: false,
    enabled: Boolean(modelId),
  });

  const modelDetailsNotFound = get(modelDetailsError, 'status') === 404;
  const modelDetailsErrorMessage = modelDetailsNotFound
    ? `Model ID ${modelId} not found (please check the URL)`
    : undefined;

  const steps = {
    [TrainingStep.ModelSelection]: {
      label: 'Select base model',
      subLabel:
        modelDetailsErrorMessage ||
        get(errors, 'stainingMethod')?.message ||
        get(errors, 'stainId')?.message ||
        get(errors, 'modelType')?.message ||
        get(errors, 'baseModel')?.message ||
        baseModelUrl ||
        'No model selected',
      content: (
        <SelectModelStep<IFormValues, 'baseModel', 'modelType', 'stainId', 'stainingMethod'>
          formMethods={formMethods as any}
          modelTypeField="modelType"
          modelUrlField="baseModel"
          slideStainField="stainId"
          stainingMethodField="stainingMethod"
          onSelectModelUrl={setIsModelSelectionStepFailed}
          stainingMethodSelectionMode={StainingMethodSelectionMode.filterStainsByMethod}
        />
      ),
    },
    [TrainingStep.AnnotationAssignments]: {
      label: 'Select annotation assignments',
      subLabel:
        get(errors, 'annotationAssignmentIds')?.message ||
        `${
          join(
            map(watch('annotationAssignmentIds'), (annotationAssignmentId) => {
              const annotationAssignment = find(annotationAssignments, { annotationAssignmentId });
              const annotationName = annotationAssignment?.name || first(annotationAssignment?.todos)?.todo;
              return `${annotationAssignmentId}${annotationName ? ` - ${annotationName}` : ''}`;
            }),
            ', '
          ) || 'No annotation assignments selected'
        }`,
      content: (
        <SelectAnnotationAssignmentsStep
          isLoading={isLoadingAnnotationAssignments}
          formMethods={formMethods}
          studyId={studyId}
          annotationAssignments={annotationAssignments}
        />
      ),
    },
    [TrainingStep.InputSlideList]: {
      label: 'Input slides',
      subLabel: get(errors, 'slideIds')?.message || `${size(watch('slideIds'))} slides selected`,
      content: <SelectSlidesStep formMethods={formMethods} annotationAssignments={annotationAssignments} />,
    },
    [TrainingStep.TargetClassesForTraining]: {
      label: 'Target classes for training',
      content: (
        <TargetClassesForTrainingStep
          formMethods={formMethods}
          annotationAssignments={annotationAssignments}
          modelDetailsResponse={modelDetailsResponse}
          modelDetailsErrorMessage={modelDetailsErrorMessage}
          isLoading={isLoadingModelDetailsResponse}
        />
      ),
    },
    [TrainingStep.PreviewTrainingPlan]: {
      label: 'Preview training plan',
      content: (
        <PreviewTrainingPlanStep
          studyId={studyId}
          formMethods={formMethods}
          annotationAssignments={annotationAssignments}
          modelDetailsResponse={modelDetailsResponse}
        />
      ),
    },
  };

  const handleSubmit = () => {
    enqueueSnackbar('Training workflow completed successfully!', { variant: 'success' });
  };

  return (
    <ActionStepper
      hideExecution
      steps={map(stepsOrder, (stepsKey) => steps[stepsKey])}
      validateStep={() => checkValidationAndSetIsStepFailed(stepsOrder[activeStep], formMethods.getValues())}
      handleSubmit={handleSubmit}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      activeStateHandlers={[activeStep, setActiveStep]}
    />
  );
};

export default TrainingDialog;
